import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { NavbarDashbord } from './navbarDashbord';
import { ArrowLeft, Save, MapPin, Building, Calendar, Image as ImageIcon, Info, Map as MapIcon } from 'lucide-react';
import { addProjet, updateProjet } from './action';
import '../styles/admin.css';

const getNumericSurface = (surfaceStr) => {
    if (!surfaceStr) return '';
    const match = parseFloat(surfaceStr);
    return isNaN(match) ? '' : match;
};

const initialState = {
    nomProjet: '',
    categorie: 'Résidentiel',
    imageProjet: '',
    societeConception: '',
    societeRéalisation: '',
    surface: '',
    annee: '',
    location: '',
    ville: '',
    description: '',
    coordonnees: {
        lat: 33.5892,
        lng: -7.6031
    }
};

export function AdminFormProjet() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const dispatch = useDispatch();
    const projectsList = useSelector(state => state.listP) || [];

    const [formData, setFormData] = useState(initialState);

    useEffect(() => {
        if (id) {
            // Find the project in the list
            const item = projectsList.find(p => p.id.toString() === id.toString());
            if (item) {
                setFormData(item);
            }
        } else {
            // Reset to empty for "add" mode
            setFormData(initialState);
        }
    }, [id, projectsList]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'lat' || name === 'lng') {
            setFormData(prev => ({
                ...prev,
                coordonnees: { ...prev.coordonnees, [name]: value }
            }));
        } else if (name === 'surface') {
            setFormData(prev => ({ ...prev, [name]: value ? `${value} m²` : '' }));
        } else if (name === 'annee') {
            setFormData(prev => ({ ...prev, [name]: value ? parseInt(value) : '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setValidationErrors({});

        const sanitizedData = {
            ...formData,
            coordonnees: {
                lat: parseFloat(formData.coordonnees?.lat) || 33.5892,
                lng: parseFloat(formData.coordonnees?.lng) || -7.6031
            }
        };

        setIsSubmitting(true);
        const action = id ? updateProjet(sanitizedData) : addProjet(sanitizedData);
        
        dispatch(action).unwrap()
            .then(() => {
                alert(id ? 'Projet mis à jour !' : 'Nouveau projet publié !');
                setIsSubmitting(false);
                navigate('/pageProjets');
            })
            .catch((err) => {
                setIsSubmitting(false);
                if (err && err.errors) {
                    setValidationErrors(err.errors);
                } else if (err && err.message) {
                    alert("Erreur: " + err.message);
                } else {
                    alert("Une erreur est survenue lors de l'enregistrement.");
                }
            });
    };

    const lat = formData.coordonnees?.lat || 33.5892;
    const lng = formData.coordonnees?.lng || -7.6031;
    const mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <button onClick={() => navigate(-1)} className="admin-back-btn">
                            <ArrowLeft size={18} /> <span className="admin-back-text">Retour</span>
                        </button>
                        <h1>{id ? 'Modifier Projet' : 'Nouveau Projet'}</h1>
                    </div>
                    <button type="submit" form="admin-projet-form" className="admin-btn-save" disabled={isSubmitting}>
                        <Save size={18} /> <span>{isSubmitting ? 'Enregistrement...' : (id ? 'Mettre à jour' : 'Publier')}</span>
                    </button>
                </header>

                <div className="admin-form-container">
                    <form id="admin-projet-form" className="admin-main-form" onSubmit={handleSave}>
                        <div className="form-sections-grid">

                            {/* Column 1: Core Details */}
                            <div className="form-column">
                                <section className="form-section-card">
                                    <div className="section-header">
                                        <Info size={20} color="var(--admin-accent)" />
                                        <h3>Détails du Projet</h3>
                                    </div>
                                    <div className="form-group">
                                        <label>Nom du Projet</label>
                                        <input type="text" name="nomProjet" value={formData.nomProjet} onChange={handleChange} placeholder="Ex: Résidence Al Hamra" required />
                                        {validationErrors.nomProjet && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.nomProjet[0]}</span>}
                                    </div>
                                    <div className="form-group-row">
                                        <div className="form-group">
                                            <label>Catégorie</label>
                                            <select name="categorie" value={formData.categorie} onChange={handleChange} required>
                                                <option value="Construction publique">Construction publique</option>
                                                <option value="Bâtiment Industriel">Bâtiment Industriel</option>
                                                <option value="Résidentiel">Résidentiel</option>
                                                <option value="Tertiaire">Tertiaire</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Année / Date</label>
                                            <input type="number" name="annee" value={formData.annee} onChange={handleChange} required />
                                            {validationErrors.annee && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.annee[0]}</span>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Description du projet</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows="5" required />
                                        {validationErrors.description && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.description[0]}</span>}
                                    </div>
                                </section>

                                <section className="form-section-card">
                                    <div className="section-header">
                                        <Building size={20} color="var(--admin-accent)" />
                                        <h3>Réalisation & Partenariats</h3>
                                    </div>
                                    <div className="form-group">
                                        <label>Société de Conception (Client)</label>
                                        <input type="text" name="societeConception" value={formData.societeConception} onChange={handleChange} required />
                                        {validationErrors.societeConception && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.societeConception[0]}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Société de Réalisation (Partenaire)</label>
                                        <input type="text" name="societeRéalisation" value={formData.societeRéalisation} onChange={handleChange} required />
                                        {validationErrors.societeRéalisation && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.societeRéalisation[0]}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Surface</label>
                                        <input type="number" name="surface" value={getNumericSurface(formData.surface)} onChange={handleChange} placeholder="Ex: 450" required />
                                        {validationErrors.surface && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.surface[0]}</span>}
                                    </div>
                                </section>
                            </div>

                            {/* Column 2: Location & Media */}
                            <div className="form-column">
                                <section className="form-section-card">
                                    <div className="section-header">
                                        <ImageIcon size={20} color="var(--admin-accent)" />
                                        <h3>Couverture & Médias</h3>
                                    </div>
                                    <div className="form-group">
                                        <label>URL de l'image de couverture</label>
                                        <input type="text" name="imageProjet" value={formData.imageProjet} onChange={handleChange} required />
                                    </div>
                                    {formData.imageProjet && (
                                        <div className="form-image-full-preview" style={{ 
                                            width: '100%', 
                                            height: '250px', 
                                            borderRadius: '12px', 
                                            overflow: 'hidden', 
                                            marginTop: '15px',
                                            border: '1px solid var(--admin-border)',
                                            background: '#f8fafc'
                                        }}>
                                            <img src={formData.imageProjet} alt="Cover Preview" style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }} />
                                        </div>
                                    )}
                                </section>

                                <section className="form-section-card">
                                    <div className="section-header">
                                        <MapIcon size={20} color="var(--admin-accent)" />
                                        <h3>Géo-localisation</h3>
                                    </div>
                                    <div className="form-group-row">
                                        <div className="form-group">
                                            <label><MapPin size={14} /> Ville</label>
                                            <input type="text" name="ville" value={formData.ville} onChange={handleChange} placeholder="Ex: Casablanca" required />
                                             {validationErrors.ville && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.ville[0]}</span>}
                                        </div>
                                        <div className="form-group">
                                            <label><MapPin size={14} /> Adresse Précise</label>
                                            <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Ex: Anfa, Casablanca" required />
                                             {validationErrors.location && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block', fontWeight: '500' }}>{validationErrors.location[0]}</span>}
                                        </div>
                                    </div>
                                    <div className="form-group-row">
                                        <div className="form-group">
                                            <label>Latitude (Lat)</label>
                                            <input type="number" step="any" name="lat" value={formData.coordonnees?.lat || ''} onChange={handleChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Longitude (Lng)</label>
                                            <input type="number" step="any" name="lng" value={formData.coordonnees?.lng || ''} onChange={handleChange} required />
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '20px', borderRadius: '14px', overflow: 'hidden', border: '2px solid var(--admin-border)', height: '250px' }}>
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            scrolling="no"
                                            marginHeight="0"
                                            marginWidth="0"
                                            src={mapSrc}
                                            style={{ filter: 'grayscale(100%) invert(90%)' }} // Match admin dark theme or clean white
                                            title="Project Location"
                                        />
                                    </div>
                                </section>
                            </div>

                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}

